import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface DKStage {
  name: string;
  nameEn: string;
  icon: string;
  description: string;
  confidence: number;
  competence: number;
  tip: string;
  color: string;
}

const STAGES: DKStage[] = [
  {
    name: '愚昧之巅',
    nameEn: 'Peak of Mt. Stupid',
    icon: '🏔️',
    description: '刚学到一点就以为自己全懂了，自信心远超实际能力',
    confidence: 95,
    competence: 10,
    tip: '保持谦虚，意识到自己不知道的东西远多于知道的',
    color: '#FF4757',
  },
  {
    name: '绝望之谷',
    nameEn: 'Valley of Despair',
    icon: '😰',
    description: '深入学习后发现自己什么都不懂，自信心跌到谷底',
    confidence: 15,
    competence: 30,
    tip: '这是成长的必经阶段，坚持就是突破',
    color: '#667EEA',
  },
  {
    name: '开悟之坡',
    nameEn: 'Slope of Enlightenment',
    icon: '📈',
    description: '持续学习积累，能力和自信都在稳步上升',
    confidence: 55,
    competence: 60,
    tip: '刻意练习 + 反馈循环 = 持续进步',
    color: '#FFD700',
  },
  {
    name: '持续高原',
    nameEn: 'Plateau of Sustainability',
    icon: '🏆',
    description: '真正的专家——知道自己知道什么，也知道自己不知道什么',
    confidence: 75,
    competence: 90,
    tip: '专家的标志是知道自己能力的边界',
    color: '#2ECC71',
  },
];

const DKCurve: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const curveProg = interpolate(frame, [30, 200], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const points: string[] = [];
  const w = 1000;
  const h = 460;
  const segments = 100;

  for (let i = 0; i <= segments * curveProg; i++) {
    const t = i / segments;
    const x = 60 + t * w;
    const y =
      h - 40 -
      (h - 80) *
        (0.95 * Math.exp(-((t - 0.12) ** 2) / 0.008) +
          0.12 * Math.exp(-((t - 0.35) ** 2) / 0.015) +
          0.7 * (1 / (1 + Math.exp(-12 * (t - 0.7)))));
    points.push(`${x},${y}`);
  }

  return (
    <svg width={w + 120} height={h}>
      {/* Axes */}
      <line x1={60} y1={h - 40} x2={w + 70} y2={h - 40} stroke="rgba(255,255,255,0.15)" strokeWidth={2} />
      <line x1={60} y1={10} x2={60} y2={h - 40} stroke="rgba(255,255,255,0.15)" strokeWidth={2} />

      <text x={w / 2 + 60} y={h - 10} fontSize={22} fill="rgba(255,255,255,0.4)" textAnchor="middle">
        能力 (Competence) →
      </text>
      <text
        x={45}
        y={h / 2}
        fontSize={22}
        fill="rgba(255,255,255,0.4)"
        textAnchor="middle"
        transform={`rotate(-90, 30, ${h / 2})`}
      >
        自信 (Confidence) →
      </text>

      {/* Curve */}
      {points.length > 1 && (
        <polyline
          points={points.join(' ')}
          fill="none"
          stroke="#FFD700"
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Stage markers */}
      {STAGES.map((stage, i) => {
        const xPositions = [0.12, 0.35, 0.62, 0.9];
        const yConfidence = [0.95, 0.15, 0.55, 0.75];
        const xPos = 60 + xPositions[i] * w;
        const yPos = h - 40 - yConfidence[i] * (h - 80);
        const markerProg = interpolate(
          curveProg,
          [xPositions[i] - 0.05, xPositions[i] + 0.05],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
        );

        return (
          <g key={stage.nameEn} opacity={markerProg}>
            <circle cx={xPos} cy={yPos} r={8} fill={stage.color} />
            <circle cx={xPos} cy={yPos} r={14} fill="none" stroke={stage.color} strokeWidth={2} opacity={0.4} />
          </g>
        );
      })}
    </svg>
  );
};

export const DunningKrugerEffectAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  return (
    <AbsoluteFill
      style={{
        background: '#0A0E27',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-25, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 60,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF4757, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          达克效应
        </h1>
        <p style={{ fontSize: 26, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Dunning-Kruger Effect · 认知偏差与能力错觉
        </p>
      </div>

      {/* DK Curve */}
      <div
        style={{
          position: 'absolute',
          top: 220,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <DKCurve frame={frame} fps={fps} />
      </div>

      {/* Stage cards - all in one row below the curve */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 16,
        }}
      >
        {STAGES.map((stage, i) => {
          const staggerDelay = i * 10;
          const cardProg = interpolate(frame, [70 + staggerDelay, 100 + staggerDelay], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          return (
            <div
              key={stage.nameEn}
              style={{
                flex: 1,
                padding: '18px 16px',
                background: `${stage.color}10`,
                border: `2px solid ${stage.color}25`,
                borderRadius: 16,
                opacity: cardProg,
                transform: `translateY(${interpolate(cardProg, [0, 1], [15, 0])}px)`,
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 36 }}>{stage.icon}</span>
              </div>
              <h3
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: stage.color,
                  margin: 0,
                  textAlign: 'center',
                }}
              >
                {stage.name}
              </h3>
              <p
                style={{
                  fontSize: 24,
                  color: 'rgba(255,255,255,0.35)',
                  textAlign: 'center',
                  margin: '4px 0 10px',
                }}
              >
                {stage.nameEn}
              </p>
              <p
                style={{
                  fontSize: 22,
                  color: '#E5E7EB',
                  lineHeight: 1.5,
                  margin: '0 0 12px',
                  textAlign: 'center',
                }}
              >
                {stage.description}
              </p>
              <div
                style={{
                  padding: '8px 12px',
                  background: `${stage.color}10`,
                  borderRadius: 10,
                  borderLeft: `3px solid ${stage.color}`,
                }}
              >
                <span style={{ fontSize: 20, color: '#D1D5DB' }}>💡 {stage.tip}</span>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
